import m from 'mithril';

import { Graph } from './graph.ts';
import { Schedule } from './schedule.ts';
import { Table } from './table.ts';
import type { Cost } from './types.ts';
import type { Mortgage } from './lib/mortgage.ts';

interface TermComparisonAttrs {
    costs: Cost[];
    /** The terms the sweep found a mortgage for, in order. */
    terms: number[];
    selected_term: number | null;
    /** The mortgage behind `selected_term`, null when there is none. */
    mortgage: Mortgage | null;
    onpick: (term: number) => void;
}

const VIEWS = ['graph', 'table', 'schedule'];

const LABELS = {
    graph: 'Graph',
    table: 'Table',
    schedule: 'Schedule',
};

export function TermComparison(): m.Component<TermComparisonAttrs> {
    let mode = 'graph';

    return {
        view: ({ attrs }) => {
            // Picking a term anywhere is a request to see it: the tab on its
            // own is a poor way in, since it needs a term before it can show
            // anything.
            const pick = (term: number) => {
                attrs.onpick(term);
                mode = 'schedule';
            };

            return m(
                '',
                m(
                    '.toolbar',
                    m(
                        '.segmented',
                        { role: 'group', 'aria-label': 'Display as' },
                        VIEWS.map((view) =>
                            m(
                                'button.segmented__option',
                                {
                                    type: 'button',
                                    // A string, not a boolean: mithril drops a
                                    // false attribute, and the styling keys off
                                    // aria-pressed="true".
                                    'aria-pressed': String(mode === view),
                                    onclick: () => {
                                        mode = view;
                                    },
                                },
                                LABELS[view],
                            ),
                        ),
                    ),
                ),
                m('.panel', body(mode, attrs, pick)),
            );
        },
    };
}

function body(
    mode: string,
    attrs: TermComparisonAttrs,
    pick: (term: number) => void,
) {
    if (mode === 'table') {
        return m(Table, { costs: attrs.costs, onpick: pick });
    }
    if (mode === 'schedule') {
        return m(ScheduleView, attrs);
    }
    return m(Graph, { costs: attrs.costs, onpick: pick });
}

/**
 * The schedule, with the term it is showing and a way to the terms either side
 * of it. Stepping rather than a dropdown: the interesting comparison is
 * always with the next term along, and it is one tap away on a phone.
 */
const ScheduleView: m.Component<TermComparisonAttrs> = {
    view: ({ attrs: { terms, selected_term, mortgage, onpick } }) => {
        if (mortgage === null || selected_term === null) {
            return m(
                'p.prose.schedule__empty',
                'Pick a term from the graph or the table to see it month by month.',
            );
        }

        const at = terms.indexOf(selected_term);

        return m('', [
            m('.schedule-picker', [
                step('‹', 'Shorter term', terms[at - 1], onpick),
                m('.schedule-picker__term', `${selected_term} years`),
                step('›', 'Longer term', terms[at + 1], onpick),
            ]),
            m(Schedule, { mortgage }),
        ]);
    },
};

function step(
    mark: string,
    label: string,
    term: number | undefined,
    onpick: (term: number) => void,
) {
    return m(
        'button.stepper',
        {
            type: 'button',
            'aria-label': label,
            disabled: term === undefined,
            onclick: () => term !== undefined && onpick(term),
        },
        mark,
    );
}
